import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { MonthCalendar } from '@mui/x-date-pickers';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Box, Dialog, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material';

import { Header } from '@app/sections/header';
import { Strings } from '@app/constants';
import { CustomAvatar, Iconify } from '@app/components';
import { useDialog, useRequestState, useSearch } from '@app/hooks';
import { useTable } from '@app/components/table';
import { OverdueList } from './overdue-list';
import SearchProject from './search-project-dialog';
import SelectAssignee from './select-assignee-dialog';
import { getOverdueTasks } from '../slice';
import { selectGetOverdueTasksState } from '../selector';
import { IOverDueExportField } from '../constants';
import { selectMyProfileFormState } from '@app/modules/my-profile';
import { IAssigneeState, IOverdueTasks } from '../type';

interface props {
  employee?: boolean;
  employeeDetails?: boolean;
}
const filterData = (data: any, selectedMonth: any, selectedProject: any, selectAssignee: any) => {
  return data?.filter((item: any) => {
    const itemMonth = dayjs(item.cardStartDate, 'DD/MM/YY HH.mm A').format('MMMM');
    const isMonthMatch = selectedMonth ? itemMonth === selectedMonth.format('MMMM') : true;
    const isProjectMatch = selectedProject
      ? item.projectName === selectedProject.projectName
      : true;
    const isAssigneeMatch = selectAssignee ? item?.assignee?.firstName === selectAssignee : true;
    return isMonthMatch && isProjectMatch && isAssigneeMatch;
  });
};

const OverdueListPage = ({ employee, employeeDetails }: props) => {
  const tableProps = useTable();
  const monthPickerDialog = useDialog();
  const projectDialog = useDialog();
  const selectAssigneeDialog = useDialog();
  const dispatch = useDispatch();

  const { data: myProfile } = useRequestState({
    stateSelector: selectMyProfileFormState,
  });

  useEffect(() => {
    if (myProfile) {
      dispatch(getOverdueTasks({ userId: myProfile?.id }));
    }
  }, [dispatch, myProfile]);

  const { data, loading } = useRequestState({
    stateSelector: selectGetOverdueTasksState,
    errorShown: true,
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAssignee, setSelectedAssignee] = useState<IAssigneeState>();
  const theme = useTheme();
  const { watch, setValue } = useForm();
  const selectedMonth = watch('selectedMonth');
  const selectedProject = watch('selectedProject');
  const selectAssignee = watch('selectedAssignee');
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleFilterOptionClick = (option: string) => {
    if (option === 'byProject') {
      projectDialog.show();
    } else if (option === 'byMonth') {
      monthPickerDialog.show();
    } else {
      selectAssigneeDialog.show();
    }
    handleClose();
  };
  const handleMonthChange = (newValue: dayjs.Dayjs | null) => {
    setValue('selectedMonth', newValue);
    monthPickerDialog.hide();
  };
  const handleProjectSelect = (project: any) => {
    setValue('selectedProject', project);
  };
  const handleAssigneeSelect = (member: any) => {
    setValue('selectedAssignee', member);
  };

  const tableHeadCsv = [
    { key: IOverDueExportField.Assignee, label: Strings.overDueTasks.assignee },
    { key: IOverDueExportField.BoardName, label: Strings.overDueTasks.boardName },
    { key: IOverDueExportField.ManagedBy, label: Strings.overDueTasks.managedBy },
    { key: IOverDueExportField.TaskName, label: Strings.overDueTasks.taskName },
    { key: IOverDueExportField.StartDateTime, label: Strings.overDueTasks.startDateTime },
    { key: IOverDueExportField.DeadLine, label: Strings.overDueTasks.deadLine },
    { key: IOverDueExportField.SubmittedTime, label: Strings.overDueTasks.submittedTime },
  ];

  const handleMonthClear = () => {
    setValue('selectedMonth', null);
  };
  const handleProjectClear = () => {
    setValue('selectedProject', null);
  };
  const handleAssigneeClear = () => {
    setValue('selectedAssignee', null);
  };

  const filteredData = filterData(data, selectedMonth, selectedProject, selectAssignee);

  const transformedData = filteredData?.map((item: IOverdueTasks) => ({
    ...item,
    assigneeName: item.assignee
      ? `${item.assignee.firstName} ${item.assignee.lastName}`.toLowerCase()
      : '',
    managedByName: item.managedBy
      ? `${item.managedBy.firstName} ${item.managedBy.lastName}`.toLowerCase()
      : '',
    cardEndTime: item.cardEndTime ? item.cardEndTime.toLowerCase() : '',
  }));

  const searchProps = useSearch({
    data: transformedData,
    fields: ['projectName', 'id', 'managedByName', 'taskName', 'assigneeName', 'cardName'],
  });

  const flattenData = (overDue: any[]): Array<Record<string, any>> => {
    if (!overDue || overDue.length === 0) return [];

    return overDue.map((task: any) => ({
      [IOverDueExportField.Assignee]: task.assigneeName ?? 'N/A',
      [IOverDueExportField.BoardName]: task.boardName ?? 'N/A',
      [IOverDueExportField.ManagedBy]: task.managedByName ?? 'N/A',
      [IOverDueExportField.TaskName]: task.cardName ?? 'N/A',
      [IOverDueExportField.StartDateTime]: task.cardStartDate
        ? dayjs(task.cardStartDate).format('D MMMM YYYY')
        : 'N/A',
      [IOverDueExportField.DeadLine]: task.cardEndDate
        ? dayjs(task.cardEndDate).format('D MMMM YYYY')
        : 'N/A',
      [IOverDueExportField.SubmittedTime]: task.completedTime
        ? dayjs(task.completedTime).format('D MMMM YYYY, h:mm A')
        : 'N/A',
    }));
  };

  return (
    <>
      <Header
        title={Strings.dashboard.overdueTasks}
        searchBarProps={{
          placeholder: Strings.common.search,
          onChange: searchProps.handleSearchChange,
        }}
        onFilterClick={handleClick}
        exportedData={{
          rows: flattenData(searchProps.filteredRows ?? []) ?? [],
          tableHeadCsv,
          csvName: 'overdue-list-page',
        }}
        additionalExportButtonTitle={!employee ? Strings.button.export : undefined}
      />
      <Stack direction={'row'}>
        {selectedMonth && (
          <Stack
            direction="row"
            alignItems="center"
            border="1px solid #919EAB"
            borderRadius="5px"
            mx={1}
            width={'auto'}
            mb={2}
          >
            <Typography textAlign="center" p={0.5} variant="body2">
              Month: {selectedMonth.format('MMMM')}
            </Typography>
            <Iconify icon={'material-symbols:close'} width={15} onClick={handleMonthClear} />
          </Stack>
        )}
        {selectedProject && (
          <Stack
            direction="row"
            alignItems="center"
            border="1px solid #919EAB"
            borderRadius="5px"
            mx={1}
            width={'auto'}
            mb={2}
          >
            <Typography textAlign="center" variant="body2" p={0.5}>
              Board Name : {selectedProject.boardName}
            </Typography>
            <Iconify icon={'material-symbols:close'} width={15} onClick={handleProjectClear} />
          </Stack>
        )}
        {selectAssignee && (
          <Stack
            direction="row"
            alignItems="center"
            border={`1px solid ${theme.palette.grey[500]}`}
            borderRadius="5px"
            mx={1}
            width="auto"
            mb={2}
          >
            <Stack direction="row" alignItems="center" spacing={1} px={1} py={0.5}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: theme.palette.grey[700], fontSize: '14px' }}
                >
                  {Strings.tasks.assignee} :
                </Box>
                <CustomAvatar
                  name={`${String(selectedAssignee?.firstName ?? '')} ${String(
                    selectedAssignee?.lastName ?? ''
                  )}`}
                  sx={{ width: 24, height: 24, fontSize: 12, ml: 1, mr: 1 }}
                  color="info"
                />
                {selectAssignee}
              </Typography>
            </Stack>
            <Iconify icon={'material-symbols:close'} width={15} onClick={handleAssigneeClear} />
          </Stack>
        )}
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          sx={{ fontSize: 13 }}
          onClick={() => {
            handleFilterOptionClick('byMonth');
          }}
        >
          {Strings.addEmployee.filterByMonth}
        </MenuItem>
        <MenuItem
          sx={{ fontSize: 13 }}
          onClick={() => {
            handleFilterOptionClick('byProject');
          }}
        >
          {Strings.addEmployee.filterByBoardName}
        </MenuItem>
        {!employee && !employeeDetails && (
          <MenuItem
            sx={{ fontSize: 13 }}
            onClick={() => {
              handleFilterOptionClick('byAssignee');
            }}
          >
            {'Filter By Assignee'}
          </MenuItem>
        )}
      </Menu>
      {monthPickerDialog.visible && (
        <Dialog
          open
          PaperProps={{
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '300px',
            },
          }}
          onClose={() => {
            monthPickerDialog.hide();
          }}
        >
          <Header variant="dialog" title={'Select Month'} onClose={monthPickerDialog.hide} />
          <MonthCalendar value={selectedMonth} onChange={handleMonthChange} />
        </Dialog>
      )}
      {projectDialog.visible && (
        <SearchProject
          open
          onClose={projectDialog.hide}
          onSelectProject={handleProjectSelect}
          data={data}
        />
      )}
      {selectAssigneeDialog.visible && (
        <SelectAssignee
          open
          onClose={selectAssigneeDialog.hide}
          onSelectAssignee={handleAssigneeSelect}
          data={data}
          setSelectedAssignee={setSelectedAssignee}
        />
      )}
      <OverdueList
        tableProps={tableProps}
        loading={loading}
        rows={searchProps.filteredRows}
        employee={employee ?? employeeDetails}
      />
    </>
  );
};

export { OverdueListPage };
